/**
 * Analytics-related types shared between frontend and backend
 */

import { MetricTrend } from './dashboard-types';

/**
 * Post analytics data including visit statistics
 */
export interface PostAnalytics {
  postId: string;
  title: string;
  slug: string;
  status: string;
  visits: number;
  url: string;
}

/**
 * Extended post analytics for content performance reports
 */
export interface ContentPerformanceMetrics {
  postId: string;
  postName: string;
  postTypes?: string; // Comma-separated list of post types
  topics?: string[]; // Array of topic names
  createdBy?: string; // Author/creator of the post
  totalViews: number;
  uniqueVisitors: number;
  uniqueAnalystCount: number;
  uniqueFirmCount: number;
  averageTimeOnPage: number; // in seconds
  totalTimeOnPage: number; // in seconds
  totalDownloads: number; // Total number of attachment downloads
  totalBookmarks: number; // Total number of bookmarks
  publishedDate?: string; // ISO string with timezone offset
  publishDate?: string; // ISO string with timezone offset
  archiveDate?: string; // ISO string with timezone offset
}

/**
 * Time series data point for charts
 */
export interface TimeSeriesDataPoint {
  date: string; // ISO date string
  value: number;
}

/**
 * Metric types for content performance reports
 */
export enum ContentPerformanceMetric {
  TOTAL_VIEWS = 'TOTAL_VIEWS',
  UNIQUE_ANALYSTS = 'UNIQUE_ANALYSTS',
  UNIQUE_FIRMS = 'UNIQUE_FIRMS',
  AVERAGE_TIME = 'AVERAGE_TIME',
  TOTAL_TIME = 'TOTAL_TIME',
  TOTAL_DOWNLOADS = 'TOTAL_DOWNLOADS',
  TOTAL_BOOKMARKS = 'TOTAL_BOOKMARKS',
}

/**
 * Chart data for content performance over time
 */
export interface ContentPerformanceChartData {
  metric: ContentPerformanceMetric;
  data: TimeSeriesDataPoint[];
  // When groupBy is specified, series contains grouped data with one series per group
  series?: {
    [groupName: string]: TimeSeriesDataPoint[];
  };
}

/**
 * Chart data for all metrics at once (optimized response)
 */
export interface ContentPerformanceChartDataAll {
  TOTAL_VIEWS: ContentPerformanceChartData;
  UNIQUE_ANALYSTS: ContentPerformanceChartData;
  UNIQUE_FIRMS: ContentPerformanceChartData;
  AVERAGE_TIME: ContentPerformanceChartData;
  TOTAL_TIME: ContentPerformanceChartData;
}

/**
 * Chart data for all metrics at once (optimized response)
 */
export interface ContentPerformanceChartDataDownload {
  TOTAL_DOWNLOADS: ContentPerformanceChartData;
  UNIQUE_ANALYSTS: ContentPerformanceChartData;
  UNIQUE_FIRMS: ContentPerformanceChartData;
}

/**
 * Chart data for bookmarks (similar structure to download chart data)
 */
export interface ContentPerformanceChartDataBookmark {
  TOTAL_BOOKMARKS: ContentPerformanceChartData;
  UNIQUE_ANALYSTS: ContentPerformanceChartData;
  UNIQUE_FIRMS: ContentPerformanceChartData;
}

/**
 * Post status filter options for content performance reports
 */
export type PostStatusFilter = 'any' | 'published' | 'archived';

/**
 * Group by options for content performance chart data
 */
export enum ContentPerformanceGroupBy {
  NONE = 'none',
  CONTENT_TYPES = 'contentTypes',
  CONTENT_TOPICS = 'contentTopics',
  FIRMS = 'firms',
}

/**
 * Filter options for content performance reports
 * Matches ContentPerformanceFiltersDto from backend
 */
export interface ContentPerformanceFilters {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  postTypes?: string[];
  topics?: string[];
  firms?: string[];
  influencers?: string[];
  influencerGroups?: string[];
  analystIds?: number[]; // Array of analyst IDs to filter by
  firmIds?: number[]; // Array of firm IDs to filter by
  status?: PostStatusFilter; // Filter by post status
  groupBy?: ContentPerformanceGroupBy; // Group chart data by: none, contentTypes, contentTopics, or firms
}

/**
 * Analyst option for filter dropdown
 */
export interface AnalystFilterOption {
  id: number;
  name: string;
}

/**
 * Firm option for filter dropdown
 */
export interface FirmFilterOption {
  id: number;
  name: string;
}

/**
 * Basic filter options with string arrays (for initial load)
 */
export interface BasicFilterOptions {
  postTypes: string[];
  topics: string[];
  firms: string[];
  influencers: string[];
}

/**
 * Available filter options for dropdowns
 */
export interface ContentPerformanceFilterOptions {
  postTypes: string[];
  topics: string[];
  analysts: AnalystFilterOption[];
  firms: FirmFilterOption[];
}

/**
 * Analyst and firm filter options based on actual Matomo data
 */
export interface AnalystFirmFilterOptions {
  analysts: AnalystFilterOption[];
  firms: FirmFilterOption[];
}

/**
 * Re-export MetricTrend from dashboard-types to avoid duplication
 */
export { MetricTrend } from './dashboard-types';

/**
 * Metric value with trend comparison
 */
export interface MetricWithTrend {
  value: number;
  percentageChange: number;
  trend: MetricTrend;
}

/**
 * Activity metrics for a specific influencer with comparison to previous period
 */
export interface InfluencerActivityMetrics {
  activeDays: MetricWithTrend;
  contentViews: MetricWithTrend;
  contentDownloads: MetricWithTrend;
  contentBookmarks: MetricWithTrend;
}

/**
 * Activity history item for a specific influencer
 */
export interface InfluencerActivityHistoryItem {
  postId: string;
  title: string;
  activityType: string;
  activityTimestamp: string; // ISO string with timezone offset
  relevantTopics: string[];
}

/**
 * Activity history item for content detail report (all activity for a single post)
 */
export interface ContentDetailHistoryItem {
  influencerName: string;
  firm: string;
  activityType: 'View' | 'Download' | 'Bookmark';
  activityTimestamp: string; // ISO string with timezone offset
  influencerId: number;
}

/**
 * Filters for content detail history API (activity for a single post)
 */
export interface ContentDetailHistoryFilters {
  postId: string;
  startDate: string;
  endDate: string;
  firmIds?: number[];
  influencerIds?: number[];
  influencerGroupId?: number | null;
}

/**
 * Category value with percentage for content preferences
 */
export interface CategoryValuePercentage {
  category: string;
  value: number;
  percentage: number;
}

/**
 * Content preferences for a specific influencer (by type and topic)
 */
export interface InfluencerContentPreferences {
  byType: CategoryValuePercentage[];
  byTopic: CategoryValuePercentage[];
}

/**
 * Influencer option for filter dropdown
 */
export interface InfluencerFilterOption {
  id: number;
  name: string;
}

/**
 * Influencer group option for filter dropdowns (id + display name)
 */
export interface InfluencerGroupFilterOption {
  id: number;
  name: string;
}

/**
 * Filter options for influencer activity report
 */
export interface InfluencerActivityFilterOptions {
  influencerGroups: InfluencerGroupFilterOption[];
  influencerTiers: number[];
  influencers: InfluencerFilterOption[];
  firms: FirmFilterOption[];
}

/**
 * Metric types for influencer activity aggregate report
 */
export enum InfluencerActivityAggregateMetric {
  TOTAL_VISITS = 'TOTAL_VISITS',
  TOTAL_TIME = 'TOTAL_TIME',
  UNIQUE_INFLUENCERS = 'UNIQUE_INFLUENCERS',
  UNIQUE_FIRMS = 'UNIQUE_FIRMS',
}

/**
 * Grouping options for influencer activity aggregate report
 */
export enum InfluencerActivityGroupBy {
  NONE = 'none',
  FIRMS = 'firms',
  TIER = 'tier',
  INFLUENCER_GROUPS = 'influencerGroups',
}

/**
 * Chart data for influencer activity aggregate over time
 */
export interface InfluencerActivityAggregateChartData {
  metric: InfluencerActivityAggregateMetric;
  data: TimeSeriesDataPoint[];
  // When groupBy is specified, series contains grouped data with one series per group
  series?: {
    [groupName: string]: TimeSeriesDataPoint[];
  };
}

/**
 * Chart data for all influencer activity metrics at once
 */
export interface InfluencerActivityAggregateChartDataAll {
  TOTAL_VISITS: InfluencerActivityAggregateChartData;
  TOTAL_TIME: InfluencerActivityAggregateChartData;
  UNIQUE_INFLUENCERS: InfluencerActivityAggregateChartData;
  UNIQUE_FIRMS: InfluencerActivityAggregateChartData;
}

/**
 * Influencer activity table row data
 */
export interface InfluencerActivityTableRow {
  influencerId: number;
  influencerName: string;
  firmName: string;
  totalVisits: number;
  totalTime: number; // in seconds
  uniquePostsAccessed: number;
}

/**
 * Grouped activity data for aggregating influencer activity metrics
 */
export interface GroupedActivityData {
  total_visits: number;
  total_time: number;
  unique_analysts: Set<number>;
  unique_firms: Set<number>;
}

/**
 * Response wrapper for content detail history API
 */
export interface ContentDetailHistoryResponse {
  title: string;
  rows: ContentDetailHistoryItem[];
}

/**
 * Response wrapper for influencer activity history API
 */
export interface InfluencerActivityHistoryResponse {
  records: InfluencerActivityHistoryItem[];
}

/**
 * Response wrapper for content performance table API
 */
export interface ContentPerformanceTableResponse {
  metrics: ContentPerformanceMetrics[];
}
